import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowRight, Building2, Check, CreditCard, Heart, Landmark, Shield, Wallet, Zap } from 'lucide-react';

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: `all 0.7s ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 border-b border-[#E2E8F0] py-2 last:border-0">
      <span className="text-sm text-[#94A3B8]">{label}</span>
      <span className="text-sm font-medium text-[#0F172A] text-right break-words">{value}</span>
    </div>
  );
}

export default function DonationPage() {
  const [settings, setSettings] = useState(null);
  const [amount, setAmount] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    api.get('/donations/settings').then((r) => {
      setSettings(r.data);
      setLoadingSettings(false);
    }).catch(() => setLoadingSettings(false));
  }, []);

  const stripe = settings?.methods?.stripe;
  const paypal = settings?.methods?.paypal;
  const bankTransfer = settings?.methods?.bank_transfer;
  const zelle = settings?.methods?.zelle;

  const handlePresetClick = (preset) => {
    setSelectedPreset(preset);
    setAmount(String(preset));
  };

  const handleCustomAmount = (val) => {
    setSelectedPreset(null);
    setAmount(val);
  };

  const handleStripeDonate = async () => {
    const donationAmount = parseFloat(amount);
    if (!donationAmount || donationAmount <= 0) {
      toast.error('Please enter a valid donation amount');
      return;
    }
    if (stripe && donationAmount < stripe.min_amount) {
      toast.error(`Minimum donation is $${stripe.min_amount}`);
      return;
    }
    if (stripe && donationAmount > stripe.max_amount) {
      toast.error(`Maximum donation is $${stripe.max_amount}`);
      return;
    }

    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await api.post('/donations/checkout', {
        amount: donationAmount,
        origin_url: origin,
      });
      if (!res.data?.url) {
        throw new Error('Stripe donations are temporarily unavailable');
      }
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to start donation process');
      setLoading(false);
    }
  };

  if (loadingSettings) {
    return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-8 h-8 border-3 border-[#1E4FA1] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!settings?.has_active_methods) {
    return (
      <div data-testid="donations-disabled" className="min-h-screen flex flex-col items-center justify-center pt-20 px-6">
        <Heart size={48} className="text-[#94A3B8] mb-4" />
        <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Donations Coming Soon</h2>
        <p className="text-[#94A3B8] mt-2 text-center max-w-xl">Online and manual donation options are currently unavailable. Please contact the ministry directly for giving options.</p>
      </div>
    );
  }

  return (
    <div data-testid="donation-page" className="pt-20">
      <section className="bg-[#1E4FA1] py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-40 h-40 border border-white/30 rounded-full" />
          <div className="absolute bottom-10 left-10 w-32 h-32 border border-white/20 rounded-full" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h1 className="mscm-hero-title font-bold text-white lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {settings.title || 'Support Our Ministry'}
          </h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl mx-auto">
            {settings.description}
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8 space-y-14">
          {(stripe?.enabled || paypal?.enabled) ? (
            <AnimatedSection>
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Online Donation Methods</h2>
                <p className="text-[#94A3B8] mt-2">Choose a secure online option to support the ministry immediately.</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                {stripe?.enabled ? (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_8px_30px_rgb(30,79,161,0.08)] p-5 sm:p-8">
                    <div className="flex min-w-0 items-center gap-3 mb-6">
                      <div className="w-14 h-14 bg-[#EBF2FA] rounded-2xl flex items-center justify-center">
                        <CreditCard size={24} className="text-[#1E4FA1]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                          {stripe.button_label || 'Donate with Stripe'}
                        </h3>
                        <p className="text-sm text-[#94A3B8]">Secure card checkout via Stripe</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-6 min-[390px]:grid-cols-3" data-testid="preset-amounts">
                      {(stripe.preset_amounts || []).map((preset) => (
                        <button
                          key={preset}
                          data-testid={`preset-${preset}`}
                          onClick={() => handlePresetClick(preset)}
                          className={`py-3 rounded-xl text-base font-semibold transition-all ${
                            selectedPreset === preset
                              ? 'bg-[#1E4FA1] text-white shadow-lg shadow-[#1E4FA1]/25'
                              : 'bg-[#F8FAFC] text-[#475569] border border-[#E2E8F0] hover:border-[#1E4FA1]/30 hover:text-[#1E4FA1]'
                          }`}
                        >
                          ${preset}
                        </button>
                      ))}
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">Custom Amount</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8] text-lg font-medium">$</span>
                        <Input
                          data-testid="custom-amount-input"
                          type="number"
                          min={stripe.min_amount}
                          max={stripe.max_amount}
                          step="0.01"
                          value={amount}
                          onChange={(e) => handleCustomAmount(e.target.value)}
                          placeholder="0.00"
                          className="pl-9 h-14 rounded-xl text-lg border-[#E2E8F0] focus:border-[#1E4FA1]"
                        />
                      </div>
                    </div>

                    <Button
                      data-testid="donate-submit-btn"
                      onClick={handleStripeDonate}
                      disabled={loading || !amount || parseFloat(amount) <= 0}
                      className="w-full bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl h-14 text-base font-semibold shadow-lg shadow-[#1E4FA1]/25 disabled:opacity-50"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Donate {amount ? `$${parseFloat(amount).toFixed(2)}` : ''} <ArrowRight size={18} />
                        </span>
                      )}
                    </Button>

                    <div className="mt-6 flex items-center gap-2 text-[#94A3B8] text-xs">
                      <Shield size={14} />
                      <span>Stripe checkout is handled securely on the server.</span>
                    </div>
                  </div>
                ) : null}

                {paypal?.enabled ? (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_8px_30px_rgb(30,79,161,0.08)] p-5 sm:p-8">
                    <div className="flex min-w-0 items-center gap-3 mb-6">
                      <div className="w-14 h-14 bg-[#EBF2FA] rounded-2xl flex items-center justify-center">
                        <Wallet size={24} className="text-[#1E4FA1]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                          {paypal.button_label || 'Donate with PayPal'}
                        </h3>
                        <p className="text-sm text-[#94A3B8]">Donate through the ministry's secure PayPal link</p>
                      </div>
                    </div>

                    <p className="text-[#475569] mb-6">
                      Use PayPal to send your support quickly. You will be redirected to the ministry&apos;s official PayPal donation page.
                    </p>

                    <Button asChild className="w-full bg-[#0A2540] hover:bg-[#153A7A] text-white rounded-xl h-14 text-base font-semibold">
                      <a href={paypal.donation_url} target="_blank" rel="noopener noreferrer">
                        Continue to PayPal <ArrowRight size={18} className="ml-2" />
                      </a>
                    </Button>

                    <div className="mt-6 flex items-center gap-2 text-[#94A3B8] text-xs">
                      <Shield size={14} />
                      <span>PayPal opens only when the secure donation link is configured.</span>
                    </div>
                  </div>
                ) : null}
              </div>
            </AnimatedSection>
          ) : null}

          {(bankTransfer?.enabled || zelle?.enabled) ? (
            <AnimatedSection delay={150}>
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Manual Donation Methods</h2>
                <p className="text-[#94A3B8] mt-2">Prefer a direct transfer? Use the details below and follow the ministry instructions.</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {bankTransfer?.enabled ? (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 sm:p-8">
                    <div className="flex min-w-0 items-center gap-3 mb-6">
                      <div className="w-14 h-14 bg-[#EBF2FA] rounded-2xl flex items-center justify-center">
                        <Landmark size={24} className="text-[#1E4FA1]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Bank Transfer</h3>
                        <p className="text-sm text-[#94A3B8]">Use these banking details for a direct transfer</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <DetailRow label="Account Holder" value={bankTransfer.account_holder} />
                      <DetailRow label="Bank Name" value={bankTransfer.bank_name} />
                      <DetailRow label="Account Number" value={bankTransfer.account_number} />
                      <DetailRow label="IBAN" value={bankTransfer.iban} />
                      <DetailRow label="SWIFT / BIC" value={bankTransfer.swift_bic} />
                      <DetailRow label="Routing Number" value={bankTransfer.routing_number} />
                    </div>
                    {bankTransfer.instructions ? (
                      <div className="mt-6 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-sm text-[#475569]">
                        {bankTransfer.instructions}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {zelle?.enabled ? (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 sm:p-8">
                    <div className="flex min-w-0 items-center gap-3 mb-6">
                      <div className="w-14 h-14 bg-[#EBF2FA] rounded-2xl flex items-center justify-center">
                        <Zap size={24} className="text-[#1E4FA1]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Zelle</h3>
                        <p className="text-sm text-[#94A3B8]">Send directly using the ministry&apos;s Zelle recipient details</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <DetailRow label="Recipient Name" value={zelle.recipient_name} />
                      <DetailRow label="Recipient Email" value={zelle.recipient_email} />
                      <DetailRow label="Recipient Phone" value={zelle.recipient_phone} />
                    </div>
                    {zelle.instructions ? (
                      <div className="mt-6 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-sm text-[#475569]">
                        {zelle.instructions}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </AnimatedSection>
          ) : null}

          <AnimatedSection delay={250}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              {[
                { label: 'Secure Giving', icon: Shield },
                { label: 'Multiple Methods', icon: Building2 },
                { label: 'Support the Mission', icon: Check },
              ].map(({ label, icon: Icon }) => (
                <div key={label} className="flex flex-col items-center gap-2 py-4">
                  <Icon size={20} className="text-[#1E4FA1]" />
                  <span className="text-xs text-[#475569] font-medium">{label}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
